/**
 * SiteOpsCard — V7 "Site Operations" card.
 * The 10-second "what's going on here?" answer for a selected site.
 * Drop into the AI pages' site side panel: <SiteOpsCard siteId={selectedSiteCode} />
 */

import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, LinearProgress, Chip, CircularProgress } from '@mui/material';
import { AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import BASE_URL from '../../config';
import { useOrg } from '../../contexts/OrgContext';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';

const SiteOpsCard = ({ siteId }) => {
  const { selectedOrgId } = useOrg();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!siteId || !selectedOrgId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const user = getAuth().currentUser;
        const token = user ? await user.getIdToken() : null;
        const res = await axios.get(`${BASE_URL}/api/sites/${siteId}/ops-summary`, {
          params: { organisation_id: selectedOrgId },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [siteId, selectedOrgId]);

  if (!siteId) return null;
  if (loading || !data) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Paper>
    );
  }

  const { establishment, maintenance, attention, last_activity } = data;
  const pct = establishment?.total_stages ? Math.round((establishment.done_stages / establishment.total_stages) * 100) : null;
  const needsAttention = (attention?.missed || 0) + (attention?.unassigned || 0);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Operations</Typography>

      {establishment ? (
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">Establishment</Typography>
            <Typography variant="caption" color="text.secondary">
              {establishment.done_stages}/{establishment.total_stages}{establishment.current_stage_name ? ` — ${establishment.current_stage_name}` : ''}
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={pct ?? 0} sx={{ height: 6, borderRadius: 3 }} />
        </Box>
      ) : (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>No establishment task yet</Typography>
      )}

      <Typography variant="body2" sx={{ mb: 0.5 }}>
        Maintenance: {maintenance.active_plans} plan{maintenance.active_plans === 1 ? '' : 's'} active
        {maintenance.next_visit && ` · next visit ${fmtDate(maintenance.next_visit)}`}
      </Typography>

      {needsAttention > 0 ? (
        <Chip
          size="small" color="warning" icon={<AlertTriangle size={12} />}
          label={`Needs attention: ${[
            attention.missed ? `${attention.missed} missed` : null,
            attention.unassigned ? `${attention.unassigned} unassigned` : null,
          ].filter(Boolean).join(' · ')}`}
          sx={{ mb: 1 }}
        />
      ) : (
        <Chip size="small" color="success" label="Needs attention: 0" sx={{ mb: 1 }} />
      )}

      {last_activity && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Last activity: {last_activity.type.replace(/_/g, ' ')} {fmtDate(last_activity.created_at)}
          {last_activity.user_name?.trim() ? ` (${last_activity.user_name.trim()})` : ''}
        </Typography>
      )}
    </Paper>
  );
};

export default SiteOpsCard;
